import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { mergeHandlers, mergeRefs, useAnchoredPanel } from "./overlay";

/** Delay before closing an interactive popover so the pointer can reach the panel. */
const INTERACTIVE_LEAVE_MS = 120;

/**
 * Theme-aware floating panel chrome (adapts in light/dark).
 * Prefer this over hard-coded inverse `bg-surface-900` tooltips, which flip
 * badly because surface-900 becomes light under `:root.dark`.
 */
export const hoverPopoverPanelClassName =
  "rounded-lg border border-surface-200 bg-surface-0 text-xs font-medium text-surface-900 shadow-popover";

export type HoverPopoverPlacement = "above" | "below";

export type HoverPopoverProps = {
  children: ReactNode;
  content: ReactNode;
  placement?: HoverPopoverPlacement;
  contentClassName?: string;
  /**
   * When true, the panel accepts pointer events and stays open while the
   * pointer is over it (for clickable actions inside the popover).
   */
  interactive?: boolean;
};

export function HoverPopover({
  children,
  content,
  placement = "above",
  contentClassName,
  interactive = false,
}: HoverPopoverProps) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const leaveTimeoutRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);

  const clearLeaveTimeout = useCallback(() => {
    if (leaveTimeoutRef.current == null) {
      return;
    }
    window.clearTimeout(leaveTimeoutRef.current);
    leaveTimeoutRef.current = null;
  }, []);

  useEffect(() => () => clearLeaveTimeout(), [clearLeaveTimeout]);

  const hide = useCallback(() => {
    clearLeaveTimeout();
    setOpen(false);
  }, [clearLeaveTimeout]);

  const show = useCallback(() => {
    clearLeaveTimeout();
    setOpen(true);
  }, [clearLeaveTimeout]);

  const scheduleHide = useCallback(() => {
    if (!interactive) {
      hide();
      return;
    }
    clearLeaveTimeout();
    leaveTimeoutRef.current = window.setTimeout(() => {
      leaveTimeoutRef.current = null;
      setOpen(false);
    }, INTERACTIVE_LEAVE_MS);
  }, [clearLeaveTimeout, hide, interactive]);

  const { panelStyle } = useAnchoredPanel({
    isOpen: open,
    onClose: hide,
    triggerRef,
    panelRef,
    placement,
    align: "center",
    passive: true,
    closeOnOutsideClick: false,
  });

  const child = Children.only(children);
  if (!isValidElement(child)) {
    return <>{children}</>;
  }

  const element = child as ReactElement<{
    ref?: React.Ref<HTMLElement>;
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
    "aria-describedby"?: string;
  }>;

  const describedBy = open
    ? [element.props["aria-describedby"], tooltipId].filter(Boolean).join(" ")
    : element.props["aria-describedby"];

  return (
    <>
      {cloneElement(element, {
        ref: mergeRefs(element.props.ref, triggerRef),
        onMouseEnter: mergeHandlers(element.props.onMouseEnter, show),
        onMouseLeave: mergeHandlers(element.props.onMouseLeave, scheduleHide),
        onFocus: mergeHandlers(element.props.onFocus, show),
        onBlur: mergeHandlers(element.props.onBlur, scheduleHide),
        "aria-describedby": describedBy || undefined,
      })}
      {open &&
        createPortal(
          <div
            id={tooltipId}
            ref={panelRef}
            role={interactive ? "group" : "tooltip"}
            style={panelStyle}
            onMouseEnter={interactive ? show : undefined}
            onMouseLeave={interactive ? scheduleHide : undefined}
            className={cn(
              "relative z-[200] w-max max-w-xs",
              interactive ? "pointer-events-auto" : "pointer-events-none",
            )}
          >
            {/* Invisible padding around the panel so the pointer can cross the
                visual gap without closing an interactive popover. Kept outside
                the clipped shell so overflow-hidden does not eat the hitbox. */}
            {interactive ? (
              <div aria-hidden className="absolute -inset-2" />
            ) : null}
            <div
              className={cn(
                "relative z-[1] animate-fade-in px-2.5 py-1.5",
                hoverPopoverPanelClassName,
                interactive && "overflow-hidden",
                contentClassName,
              )}
            >
              {content}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
